import { IAccountStorageService } from './IAccountStorageService';
import { createDefaultAccounts } from '../models/AccountModels';

const STORAGE_KEYS = {
  ACCOUNTS: 'financeTracker_accounts',
  MONTHLY_DATA: 'financeTracker_monthlyData'
};

/**
 * LocalStorage implementation of IAccountStorageService
 */
export class LocalStorageAccountService extends IAccountStorageService {
  /**
   * Load all account data from localStorage
   * @returns {Promise<import('../models/AccountModels').AccountData>}
   */
  async loadData() {
    try {
      const accounts = this._getAccounts();
      const monthlyData = this._getMonthlyData().sort((a, b) => a.month.localeCompare(b.month));

      return {
        accounts,
        monthlyData,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
      return {
        accounts: createDefaultAccounts(),
        monthlyData: []
      };
    }
  }

  /**
   * Save accounts data
   * @param {import('../models/AccountModels').Accounts} accounts
   * @returns {Promise<void>}
   */
  async saveAccounts(accounts) {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts));
    } catch (error) {
      console.error('Error saving accounts to localStorage:', error);
      throw error;
    }
  }

  /**
   * Save monthly data
   * @param {import('../models/AccountModels').MonthlyEntry[]} monthlyData
   * @returns {Promise<void>}
   */
  async saveMonthlyData(monthlyData) {
    try {
      localStorage.setItem(STORAGE_KEYS.MONTHLY_DATA, JSON.stringify(monthlyData));
    } catch (error) {
      console.error('Error saving monthly data to localStorage:', error);
      throw error;
    }
  }

  /**
   * Add a new account
   * @param {import('../models/AccountModels').Account} account
   * @returns {Promise<import('../models/AccountModels').Account>}
   */
  async addAccount(account) {
    const data = await this.loadData();
    const accountList = account.type === 'asset' ? data.accounts.assets : data.accounts.debts;
    accountList.push(account);
    await this.saveAccounts(data.accounts);
    return account;
  }

  /**
   * Remove an account
   * @param {string} accountName
   * @param {string} type - 'asset' or 'debt'
   * @returns {Promise<void>}
   */
  async removeAccount(accountName, type) {
    const data = await this.loadData();
    if (type === 'asset') {
      data.accounts.assets = data.accounts.assets.filter(a => a.name !== accountName);
    } else {
      data.accounts.debts = data.accounts.debts.filter(a => a.name !== accountName);
    }
    await this.saveAccounts(data.accounts);
  }

  /**
   * Save a monthly entry
   * @param {import('../models/AccountModels').MonthlyEntry} entry
   * @returns {Promise<void>}
   */
  async saveMonthlyEntry(entry) {
    const data = await this.loadData();
    const existingIndex = data.monthlyData.findIndex(m => m.month === entry.month);

    if (existingIndex >= 0) {
      data.monthlyData[existingIndex] = entry;
    } else {
      data.monthlyData.push(entry);
      data.monthlyData.sort((a, b) => a.month.localeCompare(b.month));
    }

    await this.saveMonthlyData(data.monthlyData);
  }

  /**
   * Clear all data
   * @returns {Promise<void>}
   */
  async clearAll() {
    localStorage.removeItem(STORAGE_KEYS.ACCOUNTS);
    localStorage.removeItem(STORAGE_KEYS.MONTHLY_DATA);
  }

  // Private helper methods
  _getAccounts() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.ACCOUNTS);
      return data ? JSON.parse(data) : createDefaultAccounts();
    } catch (error) {
      console.error('Error parsing accounts from localStorage:', error);
      return createDefaultAccounts();
    }
  }

  _getMonthlyData() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.MONTHLY_DATA);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error parsing monthly data from localStorage:', error);
      return [];
    }
  }
}
